import { lazy, Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import { isPasswordRecoveryLink } from '@/api';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import { DataProvider } from '@/lib/dataContext';
import AppLayout from '@/components/layout/AppLayout';
const Executive = lazy(() => import('./pages/Executive.jsx'));
const BySalesperson = lazy(() => import('./pages/BySalesperson.jsx'));
const Callcenter = lazy(() => import('./pages/Callcenter'));
const Coldcall = lazy(() => import('./pages/Coldcall'));
const AdminDebug = lazy(() => import('./pages/AdminDebug'));
const Roi = lazy(() => import('./pages/Roi'));
const Meta = lazy(() => import('./pages/Meta.jsx'));
const SalesPlanning = lazy(() => import('./pages/SalesPlanning'));
const Production = lazy(() => import('./pages/Production.jsx'));
const GRC = lazy(() => import('./pages/GRC'));
const RenouvellementContrat = lazy(() => import('./pages/RenouvellementContrat'));
const RenouvellementsMapping = lazy(() => import('./pages/RenouvellementsMapping'));
const RenouvellementsClients = lazy(() => import('./pages/RenouvellementsClients'));
const RenouvellementsDirection = lazy(() => import('./pages/RenouvellementsDirection'));
const RenouvellementsHistorique = lazy(() => import('./pages/RenouvellementsHistorique'));
const Recouvrement = lazy(() => import('./pages/Recouvrement'));
const Recrutement = lazy(() => import('./pages/Recrutement'));
const Taches = lazy(() => import('./pages/Taches'));
const Clients = lazy(() => import('./pages/Clients'));
const ClientFiche = lazy(() => import('./pages/ClientFiche'));
const ServiceClient = lazy(() => import('./pages/ServiceClient'));
const MonCompte = lazy(() => import('./pages/MonCompte'));
const RH = lazy(() => import('./pages/RH.jsx'));
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
const LeadQuality = lazy(() => import('./pages/LeadQuality'));
const Upsell = lazy(() => import('./pages/Upsell'));
const UpsellDiagnostic = lazy(() => import('./pages/UpsellDiagnostic'));

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, isAuthenticated, authError } = useAuth();
  const { pathname } = useLocation();

  // Un lien de récupération ouvre une vraie session : sans cette interception,
  // l'utilisateur atterrirait directement sur le dashboard sans jamais pouvoir
  // définir son mot de passe. Le chemin /reset-password permet aussi d'y venir
  // volontairement, y compris déjà connecté.
  if (isPasswordRecoveryLink || pathname === '/reset-password') {
    return <ResetPassword />;
  }

  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-border border-t-primary rounded-full animate-spin"></div>
          <p className="text-sm text-muted-foreground">Initialisation…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (authError?.type === 'user_not_registered') return <UserNotRegisteredError />;
    return <Login />;
  }

  return (
    <DataProvider>
      {/* Chaque page se charge à son ouverture : le premier écran ne télécharge plus tout le dashboard. */}
      <Suspense fallback={<div className="p-6"><div className="skeleton h-28 rounded-xl" aria-busy="true" /></div>}>
      <Routes>
        <Route element={<AppLayout />}>
          <Route path="/" element={<Executive />} />
          <Route path="/salesperson" element={<BySalesperson />} />
          <Route path="/callcenter" element={<Callcenter />} />
          <Route path="/coldcall" element={<Coldcall />} />
          <Route path="/admin" element={<AdminDebug />} />
          <Route path="/roi" element={<Roi />} />
          <Route path="/lead-quality" element={<LeadQuality />} />
          <Route path="/meta" element={<Meta />} />
          <Route path="/planning" element={<SalesPlanning />} />
          <Route path="/production" element={<Production />} />
          <Route path="/grc" element={<GRC />} />
          <Route path="/renouvellements" element={<RenouvellementsAccueil />} />
          <Route path="/renouvellements/contrats" element={<VersAccueil />} />
          <Route path="/renouvellements/mapping" element={<RenouvellementsMapping />} />
          <Route path="/renouvellements/direction" element={<RenouvellementsDirection />} />
          <Route path="/renouvellements/historique" element={<RenouvellementsHistorique />} />
          <Route path="/recouvrement" element={<Recouvrement />} />
          <Route path="/taches" element={<Taches />} />
          <Route path="/clients" element={<Clients />} />
          <Route path="/clients/:id" element={<ClientFiche />} />
          <Route path="/service-client" element={<ServiceClient />} />
          <Route path="/mon-compte" element={<MonCompte />} />
          <Route path="/rh" element={<RH />} />
          <Route path="/recrutement" element={<Recrutement />} />
          <Route path="/upsell" element={<Upsell />} />

        </Route>
        {/* Prompteur du Diagnostic : fenêtre à part, à côté de Google Meet (l'API filtre les clients). */}
        <Route path="/upsell/diagnostic/:id" element={<UpsellDiagnostic />} />
        <Route path="*" element={<PageNotFound />} />
      </Routes>
      </Suspense>
    </DataProvider>
  );
};

// /renouvellements = la vue par client. Un lien vers un contrat (?fiche=…,
// CompanyDB, recouvrement, historique) ouvre la fiche du client qui le porte.
function RenouvellementsAccueil() {
  const { search } = useLocation();
  const fiche = new URLSearchParams(search).get('fiche');
  return fiche ? <RenouvellementContrat key={fiche} id={fiche} /> : <RenouvellementsClients />;
}

// L'ancienne liste par contrat n'existe plus : ses liens mènent à l'accueil.
function VersAccueil() {
  const { search } = useLocation();
  return <Navigate to={`/renouvellements${search}`} replace />;
}

function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <AuthenticatedApp />
        </Router>
        <Toaster />
      </QueryClientProvider>
    </AuthProvider>
  );
}

export default App;
